import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'
import type { BookStatistics, UseBookStatisticsReturn } from '@/types/dashboard'

/**
 * Ultra-optimized hook for dashboard statistics using PostgreSQL stored functions
 * Performs ALL calculations on the database side - zero data transfer overhead
 */
export function useDashboardStatistics(): UseBookStatisticsReturn {
  const { user } = useAuth()

  const {
    data: statistics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboard-statistics', user?.id],
    queryFn: async (): Promise<BookStatistics> => {
      if (!user?.id) throw new Error('User not authenticated')

      // Single RPC call to get ALL statistics - maximum efficiency
      const { data, error } = await supabase.rpc('get_dashboard_statistics', {
        user_uuid: user.id,
      })

      if (error) {
        console.error('Dashboard statistics error:', error)
        throw new Error(`Failed to fetch dashboard statistics: ${error.message}`)
      }

      // Data comes directly from PostgreSQL aggregation
      return {
        totalBooks: data.totalBooks || 0,
        physicalBooks: data.physicalBooks || 0,
        digitalBooks: data.digitalBooks || 0,
        wishlistBooks: data.wishlistBooks || 0,
        borrowedBooks: data.borrowedBooks || 0,
        readTotal: data.readTotal || 0,
        readThisYear: data.readThisYear || 0,
        currentlyReading: data.currentlyReading || 0,
        unreadBooks: data.unreadBooks || 0,
        readPercentage: data.readPercentage || 0,
        physicalPercentage: data.physicalPercentage || 0,
        digitalPercentage: data.digitalPercentage || 0,
      }
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - statistics don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  return {
    statistics: statistics || getEmptyStatistics(),
    isLoading,
    error: error as Error | null,
  }
}

function getEmptyStatistics(): BookStatistics {
  return {
    totalBooks: 0,
    physicalBooks: 0,
    digitalBooks: 0,
    wishlistBooks: 0,
    borrowedBooks: 0,
    readTotal: 0,
    readThisYear: 0,
    currentlyReading: 0,
    unreadBooks: 0,
    readPercentage: 0,
    physicalPercentage: 0,
    digitalPercentage: 0,
  }
}
